import { Heading } from '../../display/Heading';
import { Tag } from '../../display/Tag';
import { Text } from '../../display/Text';
import { Icon, IconName } from '@zapier/zinnia-icons';
import React from 'react';
import styles from './PageHeader.module.scss';
import { BetaPageHeaderBase, BetaPageHeaderBaseProps } from './PageHeaderBase';

export type BetaPageHeaderProps = {
  /** Optional breadcrumbs to display at the top of the header.
   * You should use the `small` size for breadcrumbs.
   */
  breadcrumbs?: BetaPageHeaderBaseProps['topRowContent'];
  /** The main page title (H1).
   * Internally wrapped in a Heading component —
   * pass plain text or non-Heading ReactNode, not a Heading component,
   * to avoid nested headings.
   */
  title: React.ReactNode;
  /** Optional icon to display to the left of the title.
   * Pass in an IconName to use a ZinniaIcon, or pass in a custom ReactNode.
   */
  pageIcon?: IconName | Exclude<React.ReactNode, string>;
  /** Optional tag to display to the right of the title.
   * Pass in a string to use a default `Tag` component with a `simple` variant,
   * or provide custom content.
   */
  pageTag?: React.ReactNode;
  /** Actions to show at larger screen sizes */
  actions?: React.ReactNode;
  /** Actions to show at smaller screen sizes (e.g. IconButtons instead of Buttons)
   * If compactActions aren't provided, the actions will show.
   */
  compactActions?: React.ReactNode;
  /** Optional description text below the title.
   * Internally wrapped in a Text component (Body1Multiline, TextWeaker) —
   * pass plain text, not a Text component, to avoid nested elements.
   */
  description?: React.ReactNode;
  /** Optional actions/content below the description.
   * Use cases include Tabs or search/filter functionality.
   */
  additionalContent?: React.ReactNode;
  /** Optional color for the page icon. Defaults to "inherit" */
  pageIconColor?: string;
};

/**
 * A consistent page header layout component that includes support for:
 * - Breadcrumbs navigation
 * - Page title with (optional) actions
 * - Optional page description
 * - Optional additional content area.
 *
 * Based on the `BetaPageHeaderBase` component, `BetaPageHeader` is more opinionated.
 * Reach for the `BetaPageHeaderBase` component when you want to customize the header
 * content, but keep the overall layout.
 */
export const BetaPageHeader = ({
  breadcrumbs,
  title,
  actions,
  compactActions,
  description,
  additionalContent,
  pageIcon,
  pageTag,
  pageIconColor = 'inherit',
}: BetaPageHeaderProps) => {
  return (
    <BetaPageHeaderBase topRowContent={breadcrumbs}>
      <header className={styles.headerWrapper} data-zds>
        <div className={styles.titleRow}>
          <div className={styles.titleContent}>
            {pageIcon && (
              <div
                className={styles.pageIcon}
                style={{ color: pageIconColor }}
                data-zds
              >
                {typeof pageIcon === 'string' ? (
                  <Icon
                    color="inherit"
                    size="var(--page-icon-size)"
                    name={pageIcon as IconName}
                  />
                ) : (
                  pageIcon
                )}
              </div>
            )}
            <Heading className={styles.title}>{title}</Heading>
            {pageTag && (
              <div className={styles.pageTag} data-zds>
                {typeof pageTag === 'string' ? (
                  <Tag
                    variant="simple"
                    size="x-small"
                    className={styles.responsiveTag}
                  >
                    {pageTag}
                  </Tag>
                ) : (
                  pageTag
                )}
              </div>
            )}
          </div>
          {(actions ?? compactActions) && (
            <div className={styles.actionsContainer} data-zds>
              {compactActions && (
                <div className={styles.compactActions}>{compactActions}</div>
              )}
              {actions && (
                <div className={styles.defaultActions}>{actions}</div>
              )}
            </div>
          )}
        </div>
        {description && (
          <Text
            tag="p"
            type="Body1Multiline"
            color="TextWeaker"
            className={styles.description}
          >
            {description}
          </Text>
        )}
      </header>
      {additionalContent && (
        <div className={styles.additionalContent} data-zds>
          {additionalContent}
        </div>
      )}
    </BetaPageHeaderBase>
  );
};
